import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { interval, Subject, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'countdown-timer',
  templateUrl: './countdown-timer.component.html',
  styleUrls: ['./countdown-timer.component.scss'],
  imports: [DecimalPipe],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownTimerComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  durationInSeconds = input<number>(10);
  done = output<void>();

  private readonly timeLeft = signal(0);

  readonly minutes = signal(0);
  readonly seconds = signal(0);

  private isRunning = false;

  private countdownSubscription = new Subject<void>();

  ngOnInit() {
    this.updateTime(this.durationInSeconds());
    this.startCountdown();
  }

  ngOnDestroy() {
    this.completeSubscription();
  }

  private startCountdown() {
    if (this.isRunning) return;

    this.isRunning = true;

    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.countdownSubscription))
      .subscribe(() => {
        const newTime: number = this.timeLeft() - 1;
        this.updateTime(newTime);
        if (newTime <= 0) {
          this.done.emit();
          this.isRunning = false;
          this.completeSubscription();
          return;
        }
      });
  }

  stop() {
    if (!this.isRunning) return;

    this.completeSubscription();
    this.countdownSubscription = new Subject<void>();
    this.isRunning = false;
  }

  restart() {
    this.stop();
    this.updateTime(this.durationInSeconds());
    this.startCountdown();
  }

  resume() {
    if (this.timeLeft() <= 0) return;
    if (this.isRunning) return;
    this.updateTime(this.timeLeft());
    this.startCountdown();
  }

  private completeSubscription() {
    this.countdownSubscription.next();
    this.countdownSubscription.complete();
  }

  private updateTime(time: number) {
    this.timeLeft.set(time);
    this.minutes.set(Math.floor(time / 60));
    this.seconds.set(time % 60);
  }
}
